import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Button, ButtonGroup, Container, Row, Spinner } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import CsvFileInput from '../components/CsvFileInput'
import KAccess from '../components/KAccess'
import KConfirm from '../components/KConfirm'
import ServiceCostSubtitle from '../components/ServiceCostSubtitle'
import KLayerNavigation from '../components/KLayerNavigation'
import KycCkecks from '../components/KycChecks'
import {
  apiRequest,
  canEditKycForms,
  getAccessToken,
  getCompanyInfo,
  getCurrentDate,
  getUserAccess,
  isPiiEncryptionEnabled,
  isUserLoggedIn,
  setAccessToken,
  setUserAccess,
  showKAlert,
} from '../helpers'
import { ensureEnoughCredit } from '../helpers/credit.helper'
import useDebouncedValue from '../hooks/useDebouncedValue'
import { t } from '../locale'
import { Client, ClientFilter, UserAccess } from '../react-app-env'
import {
  fetchAccess,
  fetchClients,
  fetchUserEmail,
  setClientActiveStatus,
  performKycCheckMany,
  performKycCheckOneByClientId,
  softDeleteClient,
  updateClientsStatus,
  validatePinCode,
  clientDelete,
  performAmlCheckMany,
  performAmlCheckOneByClientId,
  fetchAmlCheckCost,
  updateKycStatus,
} from '../service/client'
import { getCountryData } from '../service/country'
import * as XLSX from 'xlsx'
import ClientCardView from './components/ClientCardView'
import ClientTableView from './components/ClientTableView'
import ClientToolbar from './components/ClientToolbar'
import { changeClientView } from '../service/user'
import { fetchServiceCosts, ServiceCosts } from '../service/subscription'
import { clientNotesSummary } from '../service/clientNote'

function ClientShow() {
  const navigate = useNavigate()

  const userAccess: UserAccess = getUserAccess()
  const [searchParams] = useSearchParams()
  const clientStatusParam = searchParams.get('clientStatus')
  const deleted = searchParams.get('deleted')

  const defaultFilter: ClientFilter = useMemo(
    () => ({
      clientNumber: undefined,
      keyword: '',
      isPerson: undefined,
      active: deleted !== 'notEmpty' ? clientStatusParam === 'true' : undefined,
      sortBy: 'client',
      sortDirection: 1,
      deletedAt: deleted === 'notEmpty' ? deleted : '',
      stammdatenExists: false,
      risk: 'all',
      stammdaten: null,
    }),
    [clientStatusParam, deleted]
  )

  const [clients, setClients] = useState<Client[]>([])
  const [selectedClients, setSelectedClients] = useState<Client[]>([])
  const [pin, setPin] = useState('')
  const [checkAllActive, setCheckAllActive] = useState(false)
  const [uncheckedClients, setUncheckedClients] = useState<Client[]>([])
  const [checkUnchecked, setCheckUnchecked] = useState(false)
  const [kycChecksNew, setKycChecksNew] = useState(false)
  const [access, setAccess] = useState(false)
  const [pinRequired, setPinRequired] = useState(true)
  const [checkOne, setCheckOne] = useState(false)
  const [amlCheckOne, setAmlCheckOne] = useState(false)
  const [filter, setFilter] = useState(defaultFilter)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [loading, setLoading] = useState(false)
  const [table, setTable] = useState(true)
  const [viewReady, setViewReady] = useState(false)
  const [countryRisks, setCountryRisks] = useState<Record<string, number | null>>({})
  const [countryNames, setCountryNames] = useState<Record<string, string>>({})
  const [notedClientIds, setNotedClientIds] = useState<Set<string>>(new Set())
  const [sortKey, setSortKey] = useState<'name' | 'clientNumber' | 'country' | 'lastCheck'>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [countryRiskFilter, setCountryRiskFilter] = useState<number | null>(null)
  const [kycChecks, setKycChecks] = useState<any[]>([])
  const [kycClientId, setKycClientId] = useState('')
  const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null)
  const [confirmMessage, setConfirmMessage] = useState('')
  const [globalSettings, setGlobalSettings] = useState({
    kycCheckEnabled: true,
    amlSearchEnabled: true,
  })
  const [serviceCosts, setServiceCosts] = useState<ServiceCosts | null>(null)
  const [accessSubtitle, setAccessSubtitle] = useState<ReactNode>(undefined)

  const canEdit = canEditKycForms()

  const requestConfirm = (message: string, action: () => Promise<void>) => {
    setConfirmMessage(message)
    setPendingAction(() => action)
  }

  const handleConfirm = async () => {
    if (pendingAction) await pendingAction()
    setPendingAction(null)
    setConfirmMessage('')
  }

  const handleCancelConfirm = () => {
    setPendingAction(null)
    setConfirmMessage('')
  }

  useEffect(() => {
    const getClientsAndUser = async () => {
      const response = await fetchClients(filter)

      if (response.ok && Array.isArray(response.clients)) {
        const clientsData = response.clients

        const uniqueCountries = [
          ...new Set(clientsData.map((c: Client) => c.country).filter(Boolean)),
        ]
        const promises = uniqueCountries.map(async country => {
          try {
            const countryData = await getCountryData(country as string)
            return { country, risk: countryData?.risk ?? null, name: countryData?.name ?? null }
          } catch {
            return { country, risk: null, name: null }
          }
        })
        const results = await Promise.all(promises)
        const newRisks: Record<string, number | null> = {}
        const newNames: Record<string, string> = {}
        results.forEach(({ country, risk, name }) => {
          newRisks[country as string] = risk
          if (name) newNames[country as string] = name
        })
        setCountryRisks(newRisks)
        setCountryNames(newNames)
        setClients(clientsData)

        // A never-checked client has no `risk` field at all (undefined), not `risk: null`
        // - the API drops the key entirely on insert (see client.repository.ts `create`).
        const clientsUnchecked = clientsData.filter((client: Client) => client.risk === undefined)
        setUncheckedClients(clientsUnchecked)
      }

      clientNotesSummary()
        .then(res => {
          if (res?.ok && Array.isArray(res.clientIds)) {
            setNotedClientIds(new Set(res.clientIds))
          }
        })
        .catch(() => {})

      const userResponse = await fetchUserEmail()
      if (userResponse?.user?.email) {
        localStorage.setItem('email', userResponse.user.email)
      }
      if (userResponse?.user?.clientView) {
        setTable(userResponse.user.clientView === 'card')
      }
      setViewReady(true)
    }

    getClientsAndUser()
  }, [filter])

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/global-settings`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data) setGlobalSettings(data)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    fetchServiceCosts().then(setServiceCosts)
  }, [])

  const delayedSearchKeyword = useDebouncedValue(searchKeyword, 1000)

  useEffect(() => {
    setFilter(defaultFilter)
    setSearchKeyword('')
  }, [defaultFilter])

  useEffect(() => {
    setFilter(prev => {
      if (prev.keyword === delayedSearchKeyword) return prev
      return { ...prev, keyword: delayedSearchKeyword }
    })
  }, [delayedSearchKeyword])

  const filteredClients =
    countryRiskFilter !== null
      ? clients.filter(client => Number(countryRisks[client.country]) === countryRiskFilter)
      : clients

  const sortedClients = useMemo(() => {
    const arr = [...filteredClients]
    arr.sort((a, b) => {
      let va: any
      let vb: any
      if (sortKey === 'name') {
        va = `${a.firstName ?? ''} ${a.name ?? ''}`.toLowerCase()
        vb = `${b.firstName ?? ''} ${b.name ?? ''}`.toLowerCase()
      } else if (sortKey === 'clientNumber') {
        va = a.clientNumber ?? 0
        vb = b.clientNumber ?? 0
      } else if (sortKey === 'country') {
        va = (countryNames[a.country] || a.country || '').toLowerCase()
        vb = (countryNames[b.country] || b.country || '').toLowerCase()
      } else if (sortKey === 'lastCheck') {
        va = a.lastCheck ? new Date(a.lastCheck).getTime() : 0
        vb = b.lastCheck ? new Date(b.lastCheck).getTime() : 0
      } else {
        va = a[sortKey] ?? ''
        vb = b[sortKey] ?? ''
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return arr
  }, [filteredClients, sortKey, sortDir, countryNames])

  const toggleClientActive = (client: Client) => {
    const isActivating = clientStatusParam !== 'true'
    const message = isActivating ? t('activateClient') : t('deactivateClient')
    requestConfirm(message, async () => {
      const active = deleted !== 'notEmpty' ? isActivating : undefined
      const response = await setClientActiveStatus(client, active)
      if (response.ok) {
        setClients(prev => prev.filter(c => c._id !== client._id))
      }
    })
  }

  const handleSoftDelete = (clientNumber: number, clientId: string) => {
    const isDeletedPage = deleted === 'notEmpty'
    const message = isDeletedPage ? t('deleteClient') : t('markDeleted')
    requestConfirm(message, async () => {
      if (isDeletedPage) {
        const response = await clientDelete(clientId)
        if (response.ok) {
          setClients(prev => prev.filter(c => c._id !== clientId))
        }
      } else {
        const response = await softDeleteClient(clientId, clientNumber)
        if (response.ok) {
          setClients(prev => prev.filter(c => c._id !== clientId))
        }
      }
    })
  }

  async function getAccess() {
    const result = await fetchAccess(localStorage.getItem('email'))
    if (result.accessToken) {
      setAccessToken(result.accessToken)
      setPin(result.pinCode)
      setPinRequired(result.pinRequired !== false)
      setAccess(true)
    }
  }

  const checkPin = async (pinCode: number) => {
    try {
      const result = await validatePinCode(pinCode)
      if (result?.userAccess) {
        setUserAccess(result.userAccess)
        setAccess(false)

        // Bulk toolbar checks (active / selected / unchecked) run AML when AML
        // search is enabled, otherwise SCHUFA. The single-client buttons keep
        // their own explicit routing via checkOne / amlCheckOne.
        const runBulkCheck = globalSettings.amlSearchEnabled ? amlCheckMany : kycCheck

        const response = amlCheckOne
          ? await amlSearchOne()
          : checkOne
            ? await kycCheckOne()
            : await runBulkCheck()
        if (response && !globalSettings.amlSearchEnabled) {
          ;(checkOne || checkUnchecked) && setKycChecksNew(true)
        }
      }
      setCheckUnchecked(false)
      setCheckAllActive(false)
      setAmlCheckOne(false)
    } catch (error) {
      console.error('Error validating pin code:', error)
    }
  }

  const kycCheckOne = async () => {
    try {
      setLoading(true)

      const clientId = localStorage.getItem('clientId')
      if (!clientId) {
        throw new Error('clientId is missing')
      }

      const result = await performKycCheckOneByClientId(clientId, getAccessToken() ?? '')
      const isSingleResult = Array.isArray(result?.results) && result.results.length === 1

      if (isSingleResult) {
        setKycClientId(clientId)
        setKycChecksNew(true)
        setKycChecks(result.results[0])
      } else if (result?.ok) {
        showKAlert('success', t('emailAfterCheck'))
      }
    } catch (error) {
      return []
    } finally {
      setLoading(false)
    }
  }

  const amlSearchOne = async () => {
    try {
      setLoading(true)

      const clientId = localStorage.getItem('clientId')

      if (!clientId) {
        throw new Error('clientId is missing')
      }

      const result = await performAmlCheckOneByClientId(clientId, getAccessToken() ?? '')

      // The screening runs in the background now — the user gets an email and can
      // see the result on the client page once it lands.
      if (result?.ok) {
        showKAlert('success', t('emailAfterCheck'))
      }

      return result
    } catch (error) {
      console.error('Error performing AML search:', error)
      showKAlert('danger', 'AML Search failed.')
      return null
    } finally {
      setLoading(false)
    }
  }

  const kycCheck = async () => {
    try {
      setLoading(true)

      let clientsForCheck: Client[]
      if (checkUnchecked) {
        clientsForCheck = uncheckedClients
      } else if (checkAllActive) {
        clientsForCheck = clients.filter(c => c.active)
      } else {
        clientsForCheck = selectedClients
      }

      const clientsToCheck = clientsForCheck.map(client => ({
        clientId: client._id,
        clientNumber: client.clientNumber,
        begriffe: `${client.firstName} ${client.name}`,
        dateOfBirth: client.dateOfBirth,
        land: client.country,
      }))

      if (clientsToCheck.length === 0) return

      const kycService = clientsToCheck.length === 1 ? 'schufaOne' : 'schufaMany'
      if (!(await ensureEnoughCredit(kycService))) return

      if (clientsToCheck.length > 1) {
        showKAlert('success', 'You will receive email after KYC check.')
      }

      const result = await performKycCheckMany(
        clientsToCheck,
        getAccessToken() ?? '',
        kycService,
        getCompanyInfo()
      )

      if (clientsToCheck.length === 1) {
        setKycClientId(String(clientsToCheck[0].clientId || ''))
        setKycChecksNew(true)
        setKycChecks(result.results[0])
      }
    } catch (error) {
      return []
    } finally {
      setLoading(false)
    }
  }

  const amlCheckMany = async () => {
    try {
      setLoading(true)

      let clientsForCheck: Client[]
      if (checkUnchecked) {
        clientsForCheck = uncheckedClients
      } else if (checkAllActive) {
        clientsForCheck = clients.filter(c => c.active)
      } else {
        clientsForCheck = selectedClients
      }

      const clientsToCheck = clientsForCheck.map(client => ({
        clientId: client._id,
        firstName: client.firstName,
        lastName: client.name,
        dateOfBirth: client.dateOfBirth ?? '',
        country: client.country ?? '',
        isPerson: client.isPerson === 1,
      }))

      if (clientsToCheck.length === 0) return null

      if (!(await ensureEnoughCredit('aml-search', clientsToCheck.length))) return null

      showKAlert('success', t('emailAfterCheck'))

      const result = await performAmlCheckMany(clientsToCheck, getAccessToken() ?? '')

      return result
    } catch (error) {
      console.error('Error performing AML search:', error)
      showKAlert('danger', 'AML Search failed.')
      return null
    } finally {
      setLoading(false)
    }
  }

  const kycCheckDelete = async (kycNumber: number) => {
    try {
      const targetClientId = kycClientId || localStorage.getItem('clientId') || ''
      if (!targetClientId) {
        showKAlert('danger', t('clientIdIsMissing'))
        return
      }

      await updateKycStatus(targetClientId, kycNumber, 'deleted')
      const updatedChecks = kycChecks.filter(check => check?.Eintrag?.Nummer !== kycNumber)
      setKycChecks(updatedChecks)
    } catch (error) {
      console.error('Error deleting KYC check:', error)
    }
  }

  const showAll = () => {
    setSearchKeyword('')
    setCountryRiskFilter(null)
    setFilter(defaultFilter)
  }

  const handleIsPerson = (value: string) => {
    setFilter(prev => ({ ...prev, isPerson: value }))
  }

  const handleRiskFilter = (value: boolean | null | number, risk: 'kyc' | 'country') => {
    if (risk === 'kyc') {
      setCountryRiskFilter(null)
      setFilter(prev => ({ ...prev, risk: value as boolean | null }))
    } else if (risk === 'country') {
      setFilter(prev => {
        if (prev.risk === 'all') return prev
        return { ...prev, risk: 'all' }
      })
      setCountryRiskFilter(value as number)
    }
  }

  const handleAccessClose = () => {
    setAccess(false)
    setCheckUnchecked(false)
    setCheckAllActive(false)
    setCheckOne(false)
    setAmlCheckOne(false)
    setAccessSubtitle(undefined)
  }

  const handleCsvData = async (data: any[]) => {
    try {
      await apiRequest(`${process.env.REACT_APP_API_URL}/client/import`, 'POST', {
        clients: data,
        partial: false,
      })
      window.location.reload()
    } catch {
      setClients(clients)
    }
  }

  const handleCheckboxChange = (client: Client) => {
    setSelectedClients(prevSelected => {
      if (prevSelected.some(selected => selected._id === client._id)) {
        return prevSelected.filter(selected => selected._id !== client._id)
      }
      return [...prevSelected, client]
    })
  }

  const storeClientInLocalStorage = (client: Client) => {
    localStorage.setItem('clientId', client._id)
    localStorage.setItem('name', client.name)
    localStorage.setItem('firstName', client.firstName)
    localStorage.setItem('country', client.country)
    localStorage.setItem('clientNumber', client.clientNumber.toString())
    localStorage.setItem('dateOfBirth', client.dateOfBirth ?? '')
    localStorage.setItem('isPerson', String(client.isPerson))
  }

  const handleCheck = (client: Client) => {
    storeClientInLocalStorage(client)
    setAccessSubtitle(undefined)
    setAmlCheckOne(false)
    setCheckOne(true)
    getAccess()
  }

  const handleAmlCheck = async (client: Client) => {
    storeClientInLocalStorage(client)
    setCheckOne(false)
    setAmlCheckOne(true)

    // The check also screens the client's representatives and beneficial owners,
    // so show the real total (and how many people it covers) before the user
    // confirms — not just the per-person price.
    const cost = await fetchAmlCheckCost(client._id).catch(() => null)
    setAccessSubtitle(
      <ServiceCostSubtitle
        label='AML Search'
        points={cost?.ok ? cost.points : serviceCosts?.amlSearch}
        personCount={cost?.ok ? cost.count : undefined}
        cachedCount={cost?.ok ? cost.cachedCount : undefined}
      />
    )

    getAccess()
  }

  const handleRiskChange = async (clientId: string, risik: string) => {
    try {
      const { updateClientRisk } = await import('../service/client')
      await updateClientRisk(clientId, risik as 'low' | 'medium' | 'high' | 'unknown')

      // Update local state
      setClients(
        clients.map(c =>
          c._id === clientId ? { ...c, risik: risik as 'low' | 'medium' | 'high' | 'unknown' } : c
        )
      )

      showKAlert('success', `${t('client')} ${t('risik')} ${t('updated')}.`)
    } catch (err) {
      showKAlert('danger', `${t('error')}: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const handleView = (client: Client) => {
    storeClientInLocalStorage(client)
    navigate(`/client/view/${client._id}`)
  }

  const handleSurveyForm = (clientId: string, isPerson: number) => {
    const type = isPerson === 1 ? 'person' : 'company'
    navigate(`/client/vorlaged-dc/${clientId}/${type}`)
  }

  const isDeletedPage = deleted === 'notEmpty'
  const isActivePage = clientStatusParam === 'true'

  const exportClientsToExcel = () => {
    const dataToExport = selectedClients.length > 0 ? selectedClients : sortedClients

    const rows = dataToExport.map(client => ({
      [t('clientNumber')]: client.clientNumber,
      [t('firstName')]: client.firstName,
      [t('name')]: client.name,
      [t('email')]: client.email,
      [t('gender')]: client.gender,
      [t('isPerson')]: client.isPerson,
      [t('country')]: client.country,
      [t('dateOfBirth')]: client.dateOfBirth,
      [t('phoneNumber')]: client.phoneNumber,
      [t('lastCheck')]: client.lastCheck ?? '',
      [t('active')]: client.active ? t('yes') : t('no'),
    }))

    const worksheet = XLSX.utils.json_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, t('Clients'))
    XLSX.writeFile(workbook, `clients_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const updateSelectedClients = async (deleteClients: boolean) => {
    if (selectedClients.length === 0) return

    const updates = deleteClients
      ? { deletedAt: getCurrentDate() }
      : isDeletedPage
        ? { deletedAt: '' }
        : { active: !isActivePage }

    const payload = selectedClients.map(client => ({
      clientId: client._id,
      updates,
    }))

    const response = await updateClientsStatus(payload)
    if (response.ok) {
      const selectedIds = new Set(selectedClients.map(c => c._id))
      setClients(prev => prev.filter(c => !selectedIds.has(c._id)))
      setSelectedClients([])
    }
  }

  const changeView = async (view: boolean) => {
    setTable(view)
    !view ? await changeClientView('table') : await changeClientView('card')
  }
  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>
        <main className='col-md-9 col-lg-10 ps-2 ps-md-3 px-md-4'>
          <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3'>
            <h1 className='h2'>
              {defaultFilter.deletedAt === 'notEmpty'
                ? t('deletedClients')
                : defaultFilter.active
                  ? t('Clients')
                  : t('inactiveClient')}
            </h1>
            <div className='btn-toolbar mb-2 mb-md-0'>
              {canEdit && (
                <Button
                  variant='none'
                  className='btn-sm btn-outline-primary rounded-pill'
                  onClick={() => navigate('/client/create')}
                >
                  <i className='bi bi-plus-circle'></i>
                  {t('newClient')}
                </Button>
              )}
              {canEdit && <CsvFileInput onFileLoad={handleCsvData} />}
            </div>
          </div>

          <ClientToolbar
            searchKeyword={searchKeyword}
            onSearchChange={setSearchKeyword}
            onShowAll={showAll}
            onFilterIsPerson={handleIsPerson}
            onRiskFilter={handleRiskFilter}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={(k, d) => {
              setSortKey(k)
              setSortDir(d)
            }}
            onViewChange={changeView}
          />
          <small className='text-muted'>
            <i>{t('filterInstructions')}</i>
          </small>
          {isPiiEncryptionEnabled() && (
            <div className='alert alert-info py-2 px-3 mt-3 mb-0 small' role='alert'>
              {t('encryptedSearchUnavailableClients')}
            </div>
          )}
          <hr className='my-3' />

          <div className='d-flex justify-content-between flex-nowrap align-items-center pt-1 pb-2 mb-3'>
            <div className='btn-toolbar mb-2 mb-md-0 ms-auto flex-nowrap gap-2'>
              {!isDeletedPage && isActivePage && (
                <>
                  <ButtonGroup size='sm'>
                    <Button
                      variant='outline-primary'
                      type='button'
                      className='text-nowrap no-capitalize'
                      onClick={() => {
                        setCheckAllActive(true)
                        getAccess()
                      }}
                    >
                      <i className='bi bi-check2-circle'></i>
                      &nbsp;{t('checkActive')}
                    </Button>
                    <Button
                      variant='outline-primary'
                      type='button'
                      className='text-nowrap'
                      onClick={() => getAccess()}
                    >
                      <i className='bi bi-check2-square'></i>
                      &nbsp;{t('checkSelected')}
                    </Button>
                    <Button
                      variant='outline-primary'
                      type='button'
                      className='text-nowrap'
                      onClick={() => {
                        setCheckUnchecked(true)
                        getAccess()
                      }}
                    >
                      <i className='bi bi-check2'></i>
                      &nbsp;{t('checkUnchecked')}
                    </Button>
                  </ButtonGroup>

                  <div style={{ width: '1px', height: '28px', backgroundColor: '#dee2e6' }} />

                  <Button
                    variant='none'
                    type='button'
                    className={`btn-sm rounded-pill text-nowrap ${isDeletedPage ? 'btn-outline-warning' : isActivePage ? 'btn-outline-danger' : 'btn-outline-success'} ${selectedClients.length === 0 ? 'disabled' : ''}`}
                    onClick={() =>
                      requestConfirm(
                        isDeletedPage
                          ? t('restoreSelected')
                          : isActivePage
                            ? t('deactivateSelected')
                            : t('activateSelected'),
                        async () => updateSelectedClients(false)
                      )
                    }
                  >
                    <i
                      className={`bi ${isDeletedPage ? 'bi-arrow-counterclockwise' : isActivePage ? 'bi-toggle-off' : 'bi-toggle-on'}`}
                    ></i>
                    &nbsp;
                    {isDeletedPage
                      ? t('restoreSelected')
                      : isActivePage
                        ? t('deactivateSelected')
                        : t('activateSelected')}
                  </Button>
                  {!isDeletedPage && (
                    <Button
                      variant='none'
                      type='button'
                      className={`btn-sm rounded-pill btn-outline-danger text-nowrap ${selectedClients.length === 0 ? 'disabled' : ''}`}
                      onClick={() =>
                        requestConfirm(t('deleteClients'), async () => updateSelectedClients(true))
                      }
                    >
                      <i className={'bi-toggle-off'}></i>
                      &nbsp; {t('deleteClients')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {!viewReady ? (
            <div className='d-flex justify-content-center align-items-center py-5'>
              <Spinner animation='border' variant='primary' role='status' />
            </div>
          ) : table ? (
            <ClientCardView
              clients={sortedClients}
              selectedClients={selectedClients}
              userAccess={userAccess}
              clientStatusParam={clientStatusParam}
              countryRisks={countryRisks}
              countryNames={countryNames}
              notedClientIds={notedClientIds}
              kycCheckEnabled={globalSettings.kycCheckEnabled}
              amlSearchEnabled={globalSettings.amlSearchEnabled}
              onCheckboxChange={handleCheckboxChange}
              onCheck={handleCheck}
              onAmlCheck={handleAmlCheck}
              onView={handleView}
              onSurveyForm={handleSurveyForm}
              onDeactivate={toggleClientActive}
              onDelete={handleSoftDelete}
              onRiskChange={handleRiskChange}
            />
          ) : (
            <ClientTableView
              clients={sortedClients}
              allClients={clients}
              selectedClients={selectedClients}
              userAccess={userAccess}
              clientStatusParam={clientStatusParam}
              countryRisks={countryRisks}
              countryNames={countryNames}
              notedClientIds={notedClientIds}
              kycCheckEnabled={globalSettings.kycCheckEnabled}
              amlSearchEnabled={globalSettings.amlSearchEnabled}
              onCheckboxChange={handleCheckboxChange}
              onSelectAll={setSelectedClients}
              onCheck={handleCheck}
              onAmlCheck={handleAmlCheck}
              onView={handleView}
              onSurveyForm={handleSurveyForm}
              onDeactivate={toggleClientActive}
              onDelete={handleSoftDelete}
              onRiskChange={handleRiskChange}
            />
          )}

          <div className='d-flex justify-content-end mt-3 mb-3'>
            <Button
              variant='none'
              type='button'
              className='btn-sm btn-outline-primary rounded-pill'
              onClick={exportClientsToExcel}
            >
              <i className='bi bi-file-earmark-spreadsheet'></i>
              &nbsp;{selectedClients.length > 0 ? t('exportSelected') : t('exportAll')}
            </Button>
          </div>

          {loading && (
            <div
              className='d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75'
              style={{ zIndex: 9999 }}
            >
              <Spinner animation='border' variant='primary' role='status' />
              <span className='ms-2'>{t('Loading...')}</span>
            </div>
          )}

          {access && (
            <KAccess
              show={access}
              pin={pin}
              pinRequired={pinRequired}
              subtitle={accessSubtitle}
              onConfirm={(pin: any) => checkPin(pin)}
              onClose={handleAccessClose}
              title={pinRequired ? t('pinCodeSent') : t('confirmAction')}
            />
          )}

          {kycChecksNew && (
            <KycCkecks
              show={kycChecksNew}
              kyc={kycChecks}
              onClose={() => setKycChecksNew(false)}
              onDelete={(kycNumber: number) => kycCheckDelete(kycNumber)}
            />
          )}

          <KConfirm
            show={!!pendingAction}
            message={confirmMessage}
            onConfirm={handleConfirm}
            onCancel={handleCancelConfirm}
          />
        </main>
      </Row>
    </Container>
  )
}

export default ClientShow
